import React, { useState, useCallback } from 'react';
import {
  ChevronRight, ChevronLeft, Copy, Download, CheckCircle,
  Server, Shield, Network, Wifi, Settings, BarChart3,
  Plus, Trash2, AlertTriangle, Info, Zap, Eye, EyeOff, Check, RefreshCw
} from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';

// ─────────────────────────────────────────────────────────────
// MODEL DEFINITIONS
// ─────────────────────────────────────────────────────────────
interface ModelDef {
  id: string;
  name: string;
  family: string; // 'provision' | 'cx'
  description: string;
  copperPorts: number;
  sfpPorts: number;
  sfpPlusPorts?: number;
  poe: boolean;
  stackable: boolean;
  variants: string[];
  portPrefix: string; // CX: '1/1/', Provision: ''
  sfpPrefix: string;
  maxVlans: number;
}

const MODELS: ModelDef[] = [
  {
    id: 'aruba2530-24',
    name: 'Aruba 2530-24G',
    family: 'provision',
    description: '24 port GbE + 4 SFP, Yönetilebilir, PoE opsiyonel',
    copperPorts: 24,
    sfpPorts: 4,
    poe: true,
    stackable: false,
    variants: ['2530-8G', '2530-24G', '2530-48G', '2530-24G-PoE+', '2530-48G-PoE+'],
    portPrefix: '',
    sfpPrefix: 'A',
    maxVlans: 2048,
  },
  {
    id: 'aruba2530-48',
    name: 'Aruba 2530-48G',
    family: 'provision',
    description: '48 port GbE + 4 SFP, Yönetilebilir, PoE opsiyonel',
    copperPorts: 48,
    sfpPorts: 4,
    poe: true,
    stackable: false,
    variants: ['2530-8G', '2530-24G', '2530-48G', '2530-24G-PoE+', '2530-48G-PoE+'],
    portPrefix: '',
    sfpPrefix: 'A',
    maxVlans: 2048,
  },
  {
    id: 'aruba1930-24',
    name: 'Aruba 1930-24G',
    family: 'cx',
    description: '24 port GbE + 4 SFP+, ArubaOS-CX, akıllı yönetim',
    copperPorts: 24,
    sfpPorts: 0,
    sfpPlusPorts: 4,
    poe: true,
    stackable: false,
    variants: ['1930-8G', '1930-24G', '1930-48G', '1930-24G-PoE+', '1930-48G-PoE+'],
    portPrefix: '1/1/',
    sfpPrefix: '1/1/',
    maxVlans: 4094,
  },
  {
    id: 'aruba1930-48',
    name: 'Aruba 1930-48G',
    family: 'cx',
    description: '48 port GbE + 4 SFP+, ArubaOS-CX, akıllı yönetim',
    copperPorts: 48,
    sfpPorts: 0,
    sfpPlusPorts: 4,
    poe: true,
    stackable: false,
    variants: ['1930-8G', '1930-24G', '1930-48G', '1930-24G-PoE+', '1930-48G-PoE+'],
    portPrefix: '1/1/',
    sfpPrefix: '1/1/',
    maxVlans: 4094,
  },
  {
    id: 'aruba6000-24',
    name: 'Aruba 6000-24G',
    family: 'cx',
    description: '24 port GbE + 4 SFP+, ArubaOS-CX, kurumsal düzey',
    copperPorts: 24,
    sfpPorts: 0,
    sfpPlusPorts: 4,
    poe: true,
    stackable: true,
    variants: ['6000-24G', '6000-48G', '6000-24G-PoE+', '6000-48G-PoE+'],
    portPrefix: '1/1/',
    sfpPrefix: '1/1/',
    maxVlans: 4094,
  },
  {
    id: 'aruba6000-48',
    name: 'Aruba 6000-48G',
    family: 'cx',
    description: '48 port GbE + 4 SFP+, ArubaOS-CX, kurumsal düzey',
    copperPorts: 48,
    sfpPorts: 0,
    sfpPlusPorts: 4,
    poe: true,
    stackable: true,
    variants: ['6000-24G', '6000-48G', '6000-24G-PoE+', '6000-48G-PoE+'],
    portPrefix: '1/1/',
    sfpPrefix: '1/1/',
    maxVlans: 4094,
  },
  {
    id: 'aruba6100-12',
    name: 'Aruba 6100-12G',
    family: 'cx',
    description: '12 port GbE + 2 SFP+, ArubaOS-CX, kompakt kurumsal',
    copperPorts: 12,
    sfpPorts: 0,
    sfpPlusPorts: 2,
    poe: true,
    stackable: true,
    variants: ['6100-12G', '6100-24G', '6100-48G'],
    portPrefix: '1/1/',
    sfpPrefix: '1/1/',
    maxVlans: 4094,
  },
];

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────
interface Vlan {
  id: string;
  vlan_id: number;
  name: string;
  description: string;
  is_management: boolean;
  ip_address: string;
  subnet: string;
  tagged_ports: string[];   // port IDs
  untagged_ports: string[]; // port IDs
}

interface PortConfig {
  port_id: string;
  label: string;
  type: 'copper' | 'sfp' | 'sfp+';
  mode: 'access' | 'trunk' | 'uplink' | 'disabled';
  description: string;
  native_vlan: number;
  allowed_vlans: number[];
  poe_enabled: boolean;
  speed: string;
  admin_down: boolean;
  lag_group?: number;
}

interface LagGroup {
  id: number;
  name: string;
  ports: string[];
  mode: 'active' | 'passive' | 'static';
  description: string;
  allowed_vlans: number[];
  native_vlan: number;
}

interface WizardState {
  // Step 1: Model
  model: ModelDef | null;
  selectedVariant: string;

  // Step 2: Basic
  hostname: string;
  domainName: string;
  mgmtVlan: number;
  mgmtIp: string;
  mgmtSubnet: string;
  mgmtGateway: string;
  dnsServer1: string;
  dnsServer2: string;
  ntpServer1: string;
  ntpServer2: string;
  timezone: string;

  // Step 3: Security
  enableSecret: string;
  enableSecretVisible: boolean;
  localUser: string;
  localPassword: string;
  localPasswordVisible: boolean;
  localRole: string;
  sshEnabled: boolean;
  telnetEnabled: boolean;
  httpEnabled: boolean;
  httpsEnabled: boolean;
  bannerMotd: string;
  snmpEnabled: boolean;
  snmpVersion: string;
  snmpCommunity: string;
  snmpV3User: string;
  snmpV3AuthPass: string;
  snmpV3PrivPass: string;
  snmpV3AuthProto: string;
  snmpV3PrivProto: string;
  snmpTrapHost: string;
  aaaEnabled: boolean;
  radiusServer: string;
  radiusSecret: string;
  tacacs: boolean;

  // Step 4: VLANs
  vlans: Vlan[];

  // Step 5: Ports
  ports: PortConfig[];

  // Step 6: Uplinks/LAG
  lagGroups: LagGroup[];

  // Step 7: Monitoring
  syslogServer: string;
  syslogSeverity: string;
  spanningTreeMode: string;
  spanningTreePriority: string;
  lldpEnabled: boolean;
  cdpEnabled: boolean;
}

const STEP_LABELS = [
  { id: 1, label: 'Model', icon: Server },
  { id: 2, label: 'Temel Ayarlar', icon: Settings },
  { id: 3, label: 'Güvenlik', icon: Shield },
  { id: 4, label: 'VLAN', icon: Network },
  { id: 5, label: 'Portlar', icon: Zap },
  { id: 6, label: 'Uplink / LAG', icon: Wifi },
  { id: 7, label: 'İzleme & STP', icon: BarChart3 },
  { id: 8, label: 'Konfigürasyon', icon: CheckCircle },
];

const SUBNETS = [
  '/8 (255.0.0.0)', '/16 (255.255.0.0)', '/24 (255.255.255.0)',
  '/25 (255.255.255.128)', '/26 (255.255.255.192)', '/27 (255.255.255.224)',
  '/28 (255.255.255.240)', '/29 (255.255.255.248)', '/30 (255.255.255.252)',
];

const TIMEZONES = [
  'UTC', 'Europe/Istanbul', 'Europe/London', 'Europe/Berlin',
  'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo',
];

// ─────────────────────────────────────────────────────────────
// CONFIG GENERATORS
// ─────────────────────────────────────────────────────────────
function generateProvisionConfig(s: WizardState): string {
  const lines: string[] = [];
  const model = s.model!;
  const mgmtVlan = s.vlans.find(v => v.is_management) || s.vlans[0];

  lines.push(`; ============================================================`);
  lines.push(`; Aruba / HP ProVision (ArubaOS-Switch) Konfigürasyonu`);
  lines.push(`; Model    : ${model.name} (${s.selectedVariant || model.name})`);
  lines.push(`; Hostname : ${s.hostname || 'switch'}`);
  lines.push(`; Üretildi : ${new Date().toLocaleString('tr-TR')}`);
  lines.push(`; ============================================================`);
  lines.push('');

  // Hostname
  if (s.hostname) lines.push(`hostname "${s.hostname}"`);
  if (s.domainName) lines.push(`ip dns domain-name ${s.domainName}`);
  lines.push('');

  // Timezone
  if (s.timezone && s.timezone !== 'UTC') {
    lines.push(`time timezone ${s.timezone === 'Europe/Istanbul' ? '180' : '0'}`);
  }

  // NTP
  if (s.ntpServer1) {
    lines.push(`ntp server ${s.ntpServer1}`);
    if (s.ntpServer2) lines.push(`ntp server ${s.ntpServer2}`);
    lines.push('ntp enable');
    lines.push('ntp authentication');
  }

  // DNS
  if (s.dnsServer1) {
    lines.push(`ip dns server-address priority 1 ${s.dnsServer1}`);
    if (s.dnsServer2) lines.push(`ip dns server-address priority 2 ${s.dnsServer2}`);
  }
  lines.push('');

  // Spanning Tree
  if (s.spanningTreeMode && s.spanningTreeMode !== 'none') {
    lines.push(`spanning-tree`);
    if (s.spanningTreeMode === 'rapid-pvst' || s.spanningTreeMode === 'rstp') {
      lines.push(`spanning-tree mode rapid-pvst`);
    }
    if (s.spanningTreePriority) {
      lines.push(`spanning-tree priority ${s.spanningTreePriority}`);
    }
  }
  lines.push('');

  // Password encryption
  lines.push('no telnet-server');
  if (!s.telnetEnabled) lines.push('no telnet-server');
  lines.push('service password-encryption');
  if (s.enableSecret) {
    lines.push(`enable secret ${s.enableSecret}`);
  }
  lines.push('');

  // Local user
  if (s.localUser && s.localPassword) {
    const role = s.localRole === 'admin' ? 'manager' : 'operator';
    lines.push(`password ${role} user-name "${s.localUser}" password plaintext ${s.localPassword}`);
  }
  lines.push('');

  // SSH
  if (s.sshEnabled) {
    lines.push(`crypto key generate ssh`);
    lines.push(`ip ssh`);
    lines.push(`ip ssh cipher aes256-ctr`);
    lines.push(`ip ssh mac hmac-sha2-256`);
  }

  // HTTP/HTTPS
  if (!s.httpEnabled) lines.push('no web-management');
  if (s.httpsEnabled) lines.push('web-management ssl');
  lines.push('');

  // Banner
  if (s.bannerMotd) {
    lines.push(`banner motd @`);
    lines.push(s.bannerMotd);
    lines.push(`@`);
    lines.push('');
  }

  // VLANs
  lines.push(`; --- VLAN Konfigürasyonu ---`);
  for (const vlan of s.vlans) {
    lines.push(`vlan ${vlan.vlan_id}`);
    if (vlan.name) lines.push(`   name "${vlan.name}"`);
    if (vlan.tagged_ports.length > 0) {
      lines.push(`   tagged ${vlan.tagged_ports.join(',')}`);
    }
    if (vlan.untagged_ports.length > 0) {
      lines.push(`   untagged ${vlan.untagged_ports.join(',')}`);
    }
    if (vlan.is_management && vlan.ip_address && vlan.subnet) {
      const mask = cidrToMask(vlan.subnet);
      lines.push(`   ip address ${vlan.ip_address} ${mask}`);
    }
    lines.push(`   exit`);
  }
  lines.push('');

  // Management gateway
  if (s.mgmtGateway) {
    lines.push(`ip default-gateway ${s.mgmtGateway}`);
  }
  lines.push('');

  // LLDP
  if (s.lldpEnabled) {
    lines.push('lldp run');
  } else {
    lines.push('no lldp run');
  }
  lines.push('');

  // Port configuration
  lines.push(`; --- Port Konfigürasyonu ---`);
  for (const port of s.ports) {
    if (port.mode === 'disabled') {
      lines.push(`interface ${port.port_id}`);
      lines.push(`   disable`);
      if (port.description) lines.push(`   name "${port.description}"`);
      lines.push(`   exit`);
      continue;
    }
    if (port.description || port.speed !== 'auto' || !port.poe_enabled) {
      lines.push(`interface ${port.port_id}`);
      if (port.description) lines.push(`   name "${port.description}"`);
      if (port.speed && port.speed !== 'auto') lines.push(`   speed-duplex ${port.speed}`);
      if (!port.poe_enabled && model.poe) lines.push(`   no power-over-ethernet`);
      lines.push(`   exit`);
    }
  }
  lines.push('');

  // LAG Groups (Trunk)
  if (s.lagGroups.length > 0) {
    lines.push(`; --- LAG / Trunk Grupları ---`);
    for (const lag of s.lagGroups) {
      for (const portId of lag.ports) {
        lines.push(`interface ${portId}`);
        lines.push(`   trunk ${lag.ports.join(',')} trk${lag.id} ${lag.mode === 'static' ? 'trunk' : 'lacp'}`);
        lines.push(`   exit`);
      }
      lines.push(`vlan ${lag.native_vlan || 1}`);
      lines.push(`   untagged trk${lag.id}`);
      lines.push(`   exit`);
      for (const vId of lag.allowed_vlans) {
        if (vId !== (lag.native_vlan || 1)) {
          lines.push(`vlan ${vId}`);
          lines.push(`   tagged trk${lag.id}`);
          lines.push(`   exit`);
        }
      }
      lines.push('');
    }
  }

  // SNMP
  if (s.snmpEnabled) {
    lines.push(`; --- SNMP Konfigürasyonu ---`);
    if (s.snmpVersion === 'v3') {
      lines.push(`snmpv3 user "${s.snmpV3User}" auth ${s.snmpV3AuthProto} auth-pass "${s.snmpV3AuthPass}" priv ${s.snmpV3PrivProto} priv-pass "${s.snmpV3PrivPass}"`);
    } else {
      if (s.snmpCommunity) lines.push(`snmp-server community "${s.snmpCommunity}" operator`);
    }
    if (s.snmpTrapHost) lines.push(`snmp-server host ${s.snmpTrapHost} "public"`);
    lines.push('');
  }

  // Syslog
  if (s.syslogServer) {
    lines.push(`; --- Syslog ---`);
    lines.push(`logging ${s.syslogServer}`);
    lines.push(`logging severity ${s.syslogSeverity || 'warning'}`);
    lines.push('');
  }

  // AAA / RADIUS
  if (s.aaaEnabled && s.radiusServer) {
    lines.push(`; --- AAA / RADIUS ---`);
    lines.push(`radius-server host ${s.radiusServer} key "${s.radiusSecret}"`);
    lines.push(`aaa authentication login privilege-mode`);
    lines.push(`aaa authentication login console local`);
    lines.push(`aaa authentication login ssh radius local`);
    lines.push('');
  }

  // VTY lines
  lines.push(`; --- Konsol / VTY Hatları ---`);
  lines.push(`line console`);
  lines.push(`   exec-timeout 10 0`);
  lines.push(`   exit`);
  lines.push(`line vty`);
  if (!s.telnetEnabled) lines.push(`   transport input ssh`);
  lines.push(`   exec-timeout 10 0`);
  lines.push(`   exit`);
  lines.push('');
  lines.push(`; === Konfigürasyon Tamamlandı ===`);

  return lines.join('\n');
}

function generateCXConfig(s: WizardState): string {
  const lines: string[] = [];
  const model = s.model!;

  lines.push(`! ============================================================`);
  lines.push(`! ArubaOS-CX Konfigürasyonu`);
  lines.push(`! Model    : ${model.name} (${s.selectedVariant || model.name})`);
  lines.push(`! Hostname : ${s.hostname || 'switch'}`);
  lines.push(`! Üretildi : ${new Date().toLocaleString('tr-TR')}`);
  lines.push(`! ============================================================`);
  lines.push('');

  // Hostname
  if (s.hostname) lines.push(`hostname ${s.hostname}`);
  if (s.domainName) lines.push(`ip dns domain-name ${s.domainName}`);
  lines.push('');

  // NTP
  if (s.ntpServer1) {
    lines.push(`ntp server ${s.ntpServer1}`);
    if (s.ntpServer2) lines.push(`ntp server ${s.ntpServer2}`);
  }

  // DNS
  if (s.dnsServer1) {
    lines.push(`ip dns server-address ${s.dnsServer1}`);
    if (s.dnsServer2) lines.push(`ip dns server-address ${s.dnsServer2}`);
  }
  lines.push('');

  // Spanning Tree
  if (s.spanningTreeMode && s.spanningTreeMode !== 'none') {
    lines.push(`spanning-tree mode ${s.spanningTreeMode}`);
    if (s.spanningTreePriority) {
      lines.push(`spanning-tree priority ${s.spanningTreePriority}`);
    }
  }
  lines.push('');

  // Banner
  if (s.bannerMotd) {
    lines.push(`banner motd @`);
    lines.push(s.bannerMotd);
    lines.push(`@`);
    lines.push('');
  }

  // Local user
  if (s.localUser && s.localPassword) {
    lines.push(`user ${s.localUser} password plaintext ${s.localPassword} role ${s.localRole === 'admin' ? 'administrators' : 'operators'}`);
  }
  lines.push('');

  // AAA
  if (s.aaaEnabled && s.radiusServer) {
    lines.push(`! --- AAA / RADIUS ---`);
    lines.push(`radius-server host ${s.radiusServer}`);
    lines.push(`   key plaintext ${s.radiusSecret}`);
    lines.push(`   exit`);
    lines.push(`aaa authentication login default group radius local`);
    lines.push(`aaa authorization commands default group radius local`);
    lines.push('');
  }

  // VLANs
  lines.push(`! --- VLAN Konfigürasyonu ---`);
  for (const vlan of s.vlans) {
    lines.push(`vlan ${vlan.vlan_id}`);
    if (vlan.name) lines.push(`    name ${vlan.name.replace(/\s+/g, '_')}`);
    if (vlan.description) lines.push(`    description ${vlan.description}`);
    lines.push(`    exit`);
  }
  lines.push('');

  // Management interface (VLAN SVI)
  const mgmtVlan = s.vlans.find(v => v.is_management);
  if (mgmtVlan && mgmtVlan.ip_address && mgmtVlan.subnet) {
    lines.push(`! --- Yönetim Arayüzü ---`);
    lines.push(`interface vlan ${mgmtVlan.vlan_id}`);
    lines.push(`    ip address ${mgmtVlan.ip_address}${mgmtVlan.subnet}`);
    if (s.mgmtGateway) {
      lines.push(`    ip static-route 0.0.0.0/0 ${s.mgmtGateway}`);
    }
    lines.push(`    no shutdown`);
    lines.push(`    exit`);
    if (s.mgmtGateway) {
      lines.push(`ip route 0.0.0.0/0 ${s.mgmtGateway}`);
    }
    lines.push('');
  }

  // SSH / Services
  lines.push(`! --- Erişim Servisleri ---`);
  if (s.sshEnabled) {
    lines.push(`ssh server-vrf default`);
    lines.push(`ssh server-vrf mgmt`);
  }
  if (!s.telnetEnabled) {
    lines.push(`no telnet-server`);
  }
  if (!s.httpEnabled) {
    lines.push(`no web-management http`);
  }
  if (s.httpsEnabled) {
    lines.push(`web-management https`);
  }
  lines.push('');

  // LLDP
  lines.push(`! --- LLDP ---`);
  if (s.lldpEnabled) {
    lines.push(`lldp run`);
  } else {
    lines.push(`no lldp run`);
  }
  lines.push('');

  // LAG groups first (referenced by port config)
  if (s.lagGroups.length > 0) {
    lines.push(`! --- LAG / Port-Channel Grupları ---`);
    for (const lag of s.lagGroups) {
      lines.push(`interface lag ${lag.id}`);
      lines.push(`    description ${lag.name || `LAG${lag.id}`}`);
      lines.push(`    no shutdown`);
      lines.push(`    lacp mode ${lag.mode === 'static' ? 'passive' : lag.mode}`);
      if (lag.allowed_vlans.length > 0) {
        lines.push(`    vlan trunk native ${lag.native_vlan || 1}`);
        lines.push(`    vlan trunk allowed ${lag.allowed_vlans.join(',')}`);
      }
      lines.push(`    exit`);
    }
    lines.push('');
  }

  // Ports
  lines.push(`! --- Port Konfigürasyonu ---`);
  for (const port of s.ports) {
    const hasConfig = port.mode !== 'access' || port.description || !port.poe_enabled || port.admin_down || port.allowed_vlans.length > 0 || port.native_vlan !== 1;
    if (!hasConfig && port.mode === 'access' && port.native_vlan === 1) continue;

    lines.push(`interface ${port.port_id}`);
    if (port.description) lines.push(`    description ${port.description}`);

    if (port.admin_down) {
      lines.push(`    shutdown`);
    } else {
      lines.push(`    no shutdown`);
    }

    if (port.mode === 'disabled') {
      lines.push(`    shutdown`);
    } else if (port.mode === 'access') {
      lines.push(`    vlan access ${port.native_vlan || 1}`);
    } else if (port.mode === 'trunk' || port.mode === 'uplink') {
      lines.push(`    vlan trunk native ${port.native_vlan || 1}`);
      if (port.allowed_vlans.length > 0) {
        lines.push(`    vlan trunk allowed ${port.allowed_vlans.join(',')}`);
      } else {
        lines.push(`    vlan trunk allowed all`);
      }
    }

    // LAG membership
    const lag = s.lagGroups.find(lg => lg.ports.includes(port.port_id));
    if (lag) {
      lines.push(`    lag ${lag.id}`);
    }

    // PoE
    if (model.poe && port.type === 'copper') {
      if (!port.poe_enabled) lines.push(`    no poe-lldp-detect`);
    }

    // Speed
    if (port.speed && port.speed !== 'auto') {
      lines.push(`    speed ${port.speed}`);
    }

    lines.push(`    exit`);
  }
  lines.push('');

  // SNMP
  if (s.snmpEnabled) {
    lines.push(`! --- SNMP ---`);
    if (s.snmpVersion === 'v3') {
      lines.push(`snmpv3 user ${s.snmpV3User} auth ${s.snmpV3AuthProto} auth-pass ${s.snmpV3AuthPass} priv ${s.snmpV3PrivProto} priv-pass ${s.snmpV3PrivPass}`);
    } else {
      if (s.snmpCommunity) {
        lines.push(`snmp-server community ${s.snmpCommunity} operator unrestricted`);
      }
    }
    if (s.snmpTrapHost) {
      lines.push(`snmp-server host ${s.snmpTrapHost} version ${s.snmpVersion || 'v2c'} ${s.snmpCommunity || 'public'}`);
    }
    lines.push('');
  }

  // Syslog
  if (s.syslogServer) {
    lines.push(`! --- Syslog ---`);
    lines.push(`logging ${s.syslogServer} severity ${s.syslogSeverity || 'warning'}`);
    lines.push('');
  }

  lines.push(`! === Konfigürasyon Tamamlandı ===`);

  return lines.join('\n');
}

function cidrToMask(cidr: string): string {
  const map: Record<string, string> = {
    '/8': '255.0.0.0', '/16': '255.255.0.0', '/24': '255.255.255.0',
    '/25': '255.255.255.128', '/26': '255.255.255.192', '/27': '255.255.255.224',
    '/28': '255.255.255.240', '/29': '255.255.255.248', '/30': '255.255.255.252',
  };
  const prefix = cidr.split(' ')[0];
  return map[prefix] || '255.255.255.0';
}

function generateConfig(s: WizardState): string {
  if (!s.model) return '';
  if (s.model.family === 'provision') return generateProvisionConfig(s);
  return generateCXConfig(s);
}

// ─────────────────────────────────────────────────────────────
// INITIAL STATE BUILDER
// ─────────────────────────────────────────────────────────────
function buildInitialPorts(model: ModelDef): PortConfig[] {
  const ports: PortConfig[] = [];
  // Copper
  for (let i = 1; i <= model.copperPorts; i++) {
    const id = model.family === 'provision' ? `${i}` : `${model.portPrefix}${i}`;
    ports.push({
      port_id: id,
      label: `Port ${i}`,
      type: 'copper',
      mode: 'access',
      description: '',
      native_vlan: 1,
      allowed_vlans: [],
      poe_enabled: true,
      speed: 'auto',
      admin_down: false,
    });
  }
  // SFP (provision)
  for (let i = 1; i <= model.sfpPorts; i++) {
    const id = model.family === 'provision' ? `${model.sfpPrefix}${i}` : `${model.sfpPrefix}${model.copperPorts + i}`;
    ports.push({
      port_id: id,
      label: `SFP ${i}`,
      type: 'sfp',
      mode: 'uplink',
      description: '',
      native_vlan: 1,
      allowed_vlans: [],
      poe_enabled: false,
      speed: 'auto',
      admin_down: false,
    });
  }
  // SFP+
  for (let i = 1; i <= (model.sfpPlusPorts || 0); i++) {
    const id = `${model.sfpPrefix}${model.copperPorts + i}`;
    ports.push({
      port_id: id,
      label: `SFP+ ${i}`,
      type: 'sfp+',
      mode: 'uplink',
      description: '',
      native_vlan: 1,
      allowed_vlans: [],
      poe_enabled: false,
      speed: 'auto',
      admin_down: false,
    });
  }
  return ports;
}

const DEFAULT_BANNER = `*************************************************************
* Bu sistem yetkili kullanicilara aittir.                   *
* Yetkisiz erisim yasaktir ve yasal islem baslatilacaktir.  *
*************************************************************`;

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const SwitchConfigWizard: React.FC = () => {
  const [step, setStep] = useState(1);
  const [state, setState] = useState<WizardState>({
    model: null,
    selectedVariant: '',
    hostname: '',
    domainName: '',
    mgmtVlan: 1,
    mgmtIp: '',
    mgmtSubnet: '/24 (255.255.255.0)',
    mgmtGateway: '',
    dnsServer1: '',
    dnsServer2: '',
    ntpServer1: '',
    ntpServer2: '',
    timezone: 'Europe/Istanbul',
    enableSecret: '',
    enableSecretVisible: false,
    localUser: 'admin',
    localPassword: '',
    localPasswordVisible: false,
    localRole: 'admin',
    sshEnabled: true,
    telnetEnabled: false,
    httpEnabled: false,
    httpsEnabled: true,
    bannerMotd: DEFAULT_BANNER,
    snmpEnabled: false,
    snmpVersion: 'v3',
    snmpCommunity: '',
    snmpV3User: '',
    snmpV3AuthPass: '',
    snmpV3PrivPass: '',
    snmpV3AuthProto: 'SHA',
    snmpV3PrivProto: 'AES',
    snmpTrapHost: '',
    aaaEnabled: false,
    radiusServer: '',
    radiusSecret: '',
    tacacs: false,
    vlans: [
      { id: '1', vlan_id: 1, name: 'Default', description: '', is_management: false, ip_address: '', subnet: '/24 (255.255.255.0)', tagged_ports: [], untagged_ports: [] },
    ],
    ports: [],
    lagGroups: [],
    syslogServer: '',
    syslogSeverity: 'warning',
    spanningTreeMode: 'mstp',
    spanningTreePriority: '32768',
    lldpEnabled: true,
    cdpEnabled: false,
  });

  const [generatedConfig, setGeneratedConfig] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPortBulkEdit, setShowPortBulkEdit] = useState(false);
  const [selectedPorts, setSelectedPorts] = useState<string[]>([]);
  const [bulkMode, setBulkMode] = useState<string>('access');
  const [bulkNativeVlan, setBulkNativeVlan] = useState<number>(1);
  const [bulkAllowedVlans, setBulkAllowedVlans] = useState<string>('');
  const [bulkPoe, setBulkPoe] = useState<boolean>(true);
  const [bulkDesc, setBulkDesc] = useState('');

  const update = useCallback((patch: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const selectModel = (model: ModelDef) => {
    update({ model, selectedVariant: model.variants[0] || model.name, ports: buildInitialPorts(model) });
    setStep(2);
  };

  const addVlan = () => {
    const newVlan: Vlan = {
      id: Date.now().toString(),
      vlan_id: 0,
      name: '',
      description: '',
      is_management: false,
      ip_address: '',
      subnet: '/24 (255.255.255.0)',
      tagged_ports: [],
      untagged_ports: [],
    };
    update({ vlans: [...state.vlans, newVlan] });
  };

  const updateVlan = (id: string, patch: Partial<Vlan>) => {
    update({ vlans: state.vlans.map(v => v.id === id ? { ...v, ...patch } : v) });
  };

  const removeVlan = (id: string) => {
    update({ vlans: state.vlans.filter(v => v.id !== id) });
  };

  const updatePort = (portId: string, patch: Partial<PortConfig>) => {
    update({ ports: state.ports.map(p => p.port_id === portId ? { ...p, ...patch } : p) });
  };

  const applyBulkEdit = () => {
    const allowedArr = bulkAllowedVlans
      .split(/[\s,]+/)
      .map(v => parseInt(v.trim()))
      .filter(v => !isNaN(v) && v > 0);

    update({
      ports: state.ports.map(p => {
        if (!selectedPorts.includes(p.port_id)) return p;
        return {
          ...p,
          mode: bulkMode as PortConfig['mode'],
          native_vlan: bulkNativeVlan,
          allowed_vlans: allowedArr,
          poe_enabled: bulkPoe,
          description: bulkDesc || p.description,
        };
      })
    });
    setSelectedPorts([]);
    setShowPortBulkEdit(false);
  };

  const addLag = () => {
    const nextId = (state.lagGroups.length > 0 ? Math.max(...state.lagGroups.map(l => l.id)) + 1 : 1);
    update({
      lagGroups: [...state.lagGroups, {
        id: nextId, name: `LAG ${nextId}`, ports: [], mode: 'active',
        description: '', allowed_vlans: [], native_vlan: 1,
      }]
    });
  };

  const updateLag = (id: number, patch: Partial<LagGroup>) => {
    update({ lagGroups: state.lagGroups.map(l => l.id === id ? { ...l, ...patch } : l) });
  };

  const removeLag = (id: number) => {
    update({ lagGroups: state.lagGroups.filter(l => l.id !== id) });
  };

  const handleGenerate = () => {
    const cfg = generateConfig(state);
    setGeneratedConfig(cfg);
    setStep(8);
  };

  const copyConfig = async () => {
    await navigator.clipboard.writeText(generatedConfig);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showGlobalNotification.success('Kopyalandı', 'Konfigürasyon panoya kopyalandı');
  };

  const downloadConfig = () => {
    const blob = new Blob([generatedConfig], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${state.hostname || 'switch'}-config-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── PORT VISUAL PANEL ───
  const renderPortVisual = () => {
    if (!state.model) return null;
    const portColor = (p: PortConfig) => {
      if (p.admin_down || p.mode === 'disabled') return 'bg-gray-300 dark:bg-gray-600 border-gray-400';
      if (p.mode === 'uplink') return 'bg-blue-500 border-blue-600';
      if (p.mode === 'trunk') return 'bg-purple-500 border-purple-600';
      const vlan = state.vlans.find(v => v.vlan_id === p.native_vlan);
      if (vlan?.is_management) return 'bg-orange-400 border-orange-500';
      return 'bg-green-500 border-green-600';
    };
    return (
      <div className="bg-gray-800 dark:bg-gray-900 rounded-xl p-4 mb-4 border border-gray-600">
        <div className="flex items-center gap-2 mb-3 text-xs text-gray-300">
          <span className="w-3 h-3 rounded-sm bg-green-500 inline-block" /> Access
          <span className="w-3 h-3 rounded-sm bg-purple-500 inline-block ml-2" /> Trunk
          <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block ml-2" /> Uplink/Fiber
          <span className="w-3 h-3 rounded-sm bg-orange-400 inline-block ml-2" /> Yönetim
          <span className="w-3 h-3 rounded-sm bg-gray-400 inline-block ml-2" /> Kapalı
        </div>
        <div className="flex flex-wrap gap-1.5">
          {state.ports.map(p => (
            <button
              key={p.port_id}
              title={`${p.label}${p.description ? ' - ' + p.description : ''} | ${p.mode} | VLAN ${p.native_vlan}`}
              onClick={() => {
                const s = selectedPorts.includes(p.port_id)
                  ? selectedPorts.filter(x => x !== p.port_id)
                  : [...selectedPorts, p.port_id];
                setSelectedPorts(s);
              }}
              className={`w-9 h-7 rounded border-2 text-white text-xs font-bold transition-all
                ${portColor(p)}
                ${selectedPorts.includes(p.port_id) ? 'ring-2 ring-yellow-400 scale-110' : 'hover:opacity-80'}
              `}
            >
              {p.type === 'sfp+' ? '⬦' : p.type === 'sfp' ? '◆' : p.port_id.replace('1/1/', '')}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ─────────────── STEP RENDERERS ───────────────

  const renderStep1 = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Switch Modeli Seçin</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Yapılandırmak istediğiniz Aruba switch modelini seçin.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODELS.map(m => (
          <button
            key={m.id}
            onClick={() => selectModel(m)}
            className={`text-left p-5 rounded-xl border-2 transition-all hover:shadow-lg hover:-translate-y-0.5
              ${state.model?.id === m.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 bg-white dark:bg-gray-800'
              }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${m.family === 'cx' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600' : 'bg-green-100 dark:bg-green-900/40 text-green-600'}`}>
                <Server className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">{m.name}</div>
                <div className={`text-xs font-medium px-1.5 py-0.5 rounded ${m.family === 'cx' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'}`}>
                  {m.family === 'cx' ? 'ArubaOS-CX' : 'ArubaOS-Switch'}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{m.description}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
                {m.copperPorts}× GbE
              </span>
              {m.sfpPorts > 0 && <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">{m.sfpPorts}× SFP</span>}
              {(m.sfpPlusPorts || 0) > 0 && <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">{m.sfpPlusPorts}× SFP+</span>}
              {m.poe && <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 rounded text-orange-600 dark:text-orange-400">PoE+</span>}
              {m.stackable && <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 rounded text-purple-600 dark:text-purple-400">Stackable</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Temel Ayarlar</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Hostname, yönetim IP'si ve zaman ayarları.</p>
      </div>

      {state.model && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model Varyantı</label>
          <select value={state.selectedVariant} onChange={e => update({ selectedVariant: e.target.value })}
            className="w-full md:w-72 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
            {state.model.variants.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hostname <span className="text-red-500">*</span></label>
          <input type="text" value={state.hostname} onChange={e => update({ hostname: e.target.value })}
            placeholder="örn. SW-ARGE-01"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain Adı</label>
          <input type="text" value={state.domainName} onChange={e => update({ domainName: e.target.value })}
            placeholder="örn. sirket.local"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 space-y-3">
        <div className="font-semibold text-blue-800 dark:text-blue-300 text-sm flex items-center gap-2">
          <Network className="w-4 h-4" /> Yönetim Ağı
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Yönetim VLAN</label>
            <select value={state.mgmtVlan} onChange={e => update({ mgmtVlan: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              {state.vlans.map(v => <option key={v.vlan_id} value={v.vlan_id}>VLAN {v.vlan_id} {v.name ? `(${v.name})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">IP Adresi</label>
            <input type="text" value={state.mgmtIp} onChange={e => {
              update({ mgmtIp: e.target.value });
              const mgmt = state.vlans.find(v => v.vlan_id === state.mgmtVlan);
              if (mgmt) updateVlan(mgmt.id, { ip_address: e.target.value, is_management: true });
            }}
              placeholder="192.168.1.10"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Subnet</label>
            <select value={state.mgmtSubnet} onChange={e => {
              update({ mgmtSubnet: e.target.value });
              const mgmt = state.vlans.find(v => v.vlan_id === state.mgmtVlan);
              if (mgmt) updateVlan(mgmt.id, { subnet: e.target.value });
            }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              {SUBNETS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Default Gateway</label>
            <input type="text" value={state.mgmtGateway} onChange={e => update({ mgmtGateway: e.target.value })}
              placeholder="192.168.1.1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">DNS Sunucu 1</label>
            <input type="text" value={state.dnsServer1} onChange={e => update({ dnsServer1: e.target.value })}
              placeholder="8.8.8.8"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">DNS Sunucu 2</label>
            <input type="text" value={state.dnsServer2} onChange={e => update({ dnsServer2: e.target.value })}
              placeholder="8.8.4.4"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">NTP Sunucu 1</label>
          <input type="text" value={state.ntpServer1} onChange={e => update({ ntpServer1: e.target.value })}
            placeholder="0.tr.pool.ntp.org"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">NTP Sunucu 2</label>
          <input type="text" value={state.ntpServer2} onChange={e => update({ ntpServer2: e.target.value })}
            placeholder="1.tr.pool.ntp.org"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zaman Dilimi</label>
          <select value={state.timezone} onChange={e => update({ timezone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Güvenlik Ayarları</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Erişim kontrolü, şifreleme ve güvenlik politikaları.</p>
      </div>

      {/* Credentials */}
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 space-y-3 border border-red-200 dark:border-red-800">
        <div className="font-semibold text-red-800 dark:text-red-300 text-sm flex items-center gap-2">
          <Shield className="w-4 h-4" /> Kimlik Bilgileri
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Enable Secret (Privileged)</label>
            <div className="relative">
              <input type={state.enableSecretVisible ? 'text' : 'password'} value={state.enableSecret}
                onChange={e => update({ enableSecret: e.target.value })}
                className="w-full px-3 py-2 pr-9 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              <button onClick={() => update({ enableSecretVisible: !state.enableSecretVisible })}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600">
                {state.enableSecretVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Kullanıcı Adı</label>
            <input type="text" value={state.localUser} onChange={e => update({ localUser: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Kullanıcı Şifresi</label>
            <div className="relative">
              <input type={state.localPasswordVisible ? 'text' : 'password'} value={state.localPassword}
                onChange={e => update({ localPassword: e.target.value })}
                className="w-full px-3 py-2 pr-9 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              <button onClick={() => update({ localPasswordVisible: !state.localPasswordVisible })}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600">
                {state.localPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Kullanıcı Rolü</label>
            <select value={state.localRole} onChange={e => update({ localRole: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              <option value="admin">Admin / Manager</option>
              <option value="operator">Operator (Read-Only)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Access Services */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Erişim Protokolleri</div>
          {[
            { key: 'sshEnabled', label: 'SSH (Tavsiye edilir)', safe: true },
            { key: 'telnetEnabled', label: 'Telnet (GÜVENSİZ)', safe: false },
            { key: 'httpsEnabled', label: 'HTTPS Yönetim', safe: true },
            { key: 'httpEnabled', label: 'HTTP Yönetim (GÜVENSİZ)', safe: false },
          ].map(({ key, label, safe }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={state[key as keyof WizardState] as boolean}
                onChange={e => update({ [key]: e.target.checked })}
                className="w-4 h-4 accent-blue-600" />
              <span className={`text-sm ${safe ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400 font-medium'}`}>{label}</span>
              {!safe && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
            </label>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Ağ Keşfi</div>
          {[
            { key: 'lldpEnabled', label: 'LLDP (Link Layer Discovery)' },
            { key: 'cdpEnabled', label: 'CDP (Cisco Discovery Protocol)' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={state[key as keyof WizardState] as boolean}
                onChange={e => update({ [key]: e.target.checked })}
                className="w-4 h-4 accent-blue-600" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Banner */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Giriş Uyarı Mesajı (Banner MOTD)</label>
        <textarea value={state.bannerMotd} onChange={e => update({ bannerMotd: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono" />
      </div>

      {/* SNMP */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={state.snmpEnabled} onChange={e => update({ snmpEnabled: e.target.checked })} className="w-4 h-4 accent-blue-600" />
          <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">SNMP Etkinleştir</span>
        </label>
        {state.snmpEnabled && (
          <div className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SNMP Versiyonu</label>
              <select value={state.snmpVersion} onChange={e => update({ snmpVersion: e.target.value })}
                className="w-full md:w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                <option value="v3">SNMPv3 (Tavsiye)</option>
                <option value="v2c">SNMPv2c</option>
                <option value="v1">SNMPv1 (Eski)</option>
              </select>
            </div>
            {state.snmpVersion === 'v3' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SNMPv3 Kullanıcı</label>
                  <input type="text" value={state.snmpV3User} onChange={e => update({ snmpV3User: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Auth Şifresi</label>
                  <input type="password" value={state.snmpV3AuthPass} onChange={e => update({ snmpV3AuthPass: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Auth Protokol</label>
                  <select value={state.snmpV3AuthProto} onChange={e => update({ snmpV3AuthProto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                    <option value="SHA">SHA (Tavsiye)</option>
                    <option value="MD5">MD5</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Priv Şifresi</label>
                  <input type="password" value={state.snmpV3PrivPass} onChange={e => update({ snmpV3PrivPass: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Priv Protokol</label>
                  <select value={state.snmpV3PrivProto} onChange={e => update({ snmpV3PrivProto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                    <option value="AES">AES (Tavsiye)</option>
                    <option value="DES">DES</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SNMP Trap Host</label>
                  <input type="text" value={state.snmpTrapHost} onChange={e => update({ snmpTrapHost: e.target.value })}
                    placeholder="192.168.1.5"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Community String</label>
                  <input type="text" value={state.snmpCommunity} onChange={e => update({ snmpCommunity: e.target.value })}
                    placeholder="public/private KULLANMAYIN"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                  {(state.snmpCommunity === 'public' || state.snmpCommunity === 'private') && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Varsayılan community güvenlik açığı oluşturur!</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SNMP Trap Host</label>
                  <input type="text" value={state.snmpTrapHost} onChange={e => update({ snmpTrapHost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AAA / RADIUS */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={state.aaaEnabled} onChange={e => update({ aaaEnabled: e.target.checked })} className="w-4 h-4 accent-blue-600" />
          <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">AAA / RADIUS Kimlik Doğrulaması</span>
        </label>
        {state.aaaEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">RADIUS Sunucu IP</label>
              <input type="text" value={state.radiusServer} onChange={e => update({ radiusServer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Shared Secret</label>
              <input type="password" value={state.radiusSecret} onChange={e => update({ radiusSecret: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">VLAN Yapılandırması</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Ağ segmentasyonu için VLAN'ları tanımlayın.</p>
        </div>
        <button onClick={addVlan} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
          <Plus className="w-4 h-4" /> VLAN Ekle
        </button>
      </div>

      <div className="space-y-3">
        {state.vlans.map((vlan) => (
          <div key={vlan.id} className={`rounded-xl border-2 p-4 ${vlan.is_management ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VLAN ID</label>
                <input type="number" min="1" max="4094" value={vlan.vlan_id}
                  onChange={e => updateVlan(vlan.id, { vlan_id: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono font-bold" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VLAN Adı</label>
                <input type="text" value={vlan.name}
                  onChange={e => updateVlan(vlan.id, { name: e.target.value })}
                  placeholder="örn. Data, Yönetim, VoIP"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Açıklama</label>
                <input type="text" value={vlan.description}
                  onChange={e => updateVlan(vlan.id, { description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input type="checkbox" checked={vlan.is_management}
                    onChange={e => {
                      // Only one management VLAN
                      const newVlans = state.vlans.map(v => ({ ...v, is_management: v.id === vlan.id ? e.target.checked : false }));
                      update({ vlans: newVlans });
                    }}
                    className="w-4 h-4 accent-orange-500" />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Yönetim VLAN'ı</span>
                </label>
                {state.vlans.length > 1 && (
                  <button onClick={() => removeVlan(vlan.id)} className="pb-2 text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {vlan.is_management && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-orange-200 dark:border-orange-800">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">IP Adresi</label>
                  <input type="text" value={vlan.ip_address} onChange={e => updateVlan(vlan.id, { ip_address: e.target.value })}
                    placeholder="192.168.1.10"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Subnet</label>
                  <select value={vlan.subnet} onChange={e => updateVlan(vlan.id, { subnet: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                    {SUBNETS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center gap-2 mt-4 text-xs text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 rounded-lg px-3 py-2">
                    <Info className="w-3.5 h-3.5 flex-shrink-0" />
                    Gateway adım 2'de girildi
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {state.vlans.length < 2 && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            İyi bir ağ tasarımı için en az 3-4 VLAN önerilir: <strong>Yönetim</strong>, <strong>Data/Kullanıcı</strong>, <strong>VoIP</strong>, <strong>Misafir</strong>.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep5 = () => {
    if (!state.model) return null;
    const copperPorts = state.ports.filter(p => p.type === 'copper');
    const fiberPorts = state.ports.filter(p => p.type === 'sfp' || p.type === 'sfp+');
    const vlanOptions = state.vlans.filter(v => v.vlan_id > 0);

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Port Yapılandırması</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Port üzerine tıklayarak seçin, toplu düzenleme için birden fazla port seçin.</p>
          </div>
          {selectedPorts.length > 0 && (
            <button onClick={() => setShowPortBulkEdit(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
              <Settings className="w-4 h-4" />
              {selectedPorts.length} Port Seçildi — Toplu Düzenle
            </button>
          )}
          {selectedPorts.length === 0 && (
            <button onClick={() => setSelectedPorts(state.ports.map(p => p.port_id))}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Tümünü Seç
            </button>
          )}
        </div>

        {/* Port diagram */}
        {renderPortVisual()}

        {/* Bulk Edit Panel */}
        {showPortBulkEdit && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 rounded-xl p-4 space-y-4">
            <div className="font-semibold text-yellow-800 dark:text-yellow-300 text-sm">{selectedPorts.length} port toplu düzenleme</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Mod</label>
                <select value={bulkMode} onChange={e => setBulkMode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100">
                  <option value="access">Access</option>
                  <option value="trunk">Trunk</option>
                  <option value="uplink">Uplink</option>
                  <option value="disabled">Devre Dışı</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Native / Access VLAN</label>
                <select value={bulkNativeVlan} onChange={e => setBulkNativeVlan(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100">
                  {vlanOptions.map(v => <option key={v.vlan_id} value={v.vlan_id}>VLAN {v.vlan_id} {v.name ? `(${v.name})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Allowed VLANs (trunk)</label>
                <input type="text" value={bulkAllowedVlans} onChange={e => setBulkAllowedVlans(e.target.value)}
                  placeholder="10,20,30"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Açıklama</label>
                <input type="text" value={bulkDesc} onChange={e => setBulkDesc(e.target.value)}
                  placeholder="İsteğe bağlı"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100" />
              </div>
            </div>
            {state.model.poe && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bulkPoe} onChange={e => setBulkPoe(e.target.checked)} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">PoE Etkin</span>
              </label>
            )}
            <div className="flex gap-3">
              <button onClick={applyBulkEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">Uygula</button>
              <button onClick={() => { setShowPortBulkEdit(false); setSelectedPorts([]); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm">İptal</button>
            </div>
          </div>
        )}

        {/* Fiber Ports */}
        {fiberPorts.length > 0 && (
          <div>
            <div className="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-3 flex items-center gap-2">
              <span className="text-blue-500">◆</span> Fiber / SFP Portları ({fiberPorts.length} port)
            </div>
            <div className="space-y-2">
              {fiberPorts.map(port => (
                <div key={port.port_id} className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border transition-all
                  ${selectedPorts.includes(port.port_id) ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-400'}`}
                  onClick={() => setSelectedPorts(prev => prev.includes(port.port_id) ? prev.filter(x => x !== port.port_id) : [...prev, port.port_id])}>
                  <div className="flex items-center gap-2 w-28">
                    <div className={`w-6 h-5 rounded text-white text-xs flex items-center justify-center font-bold
                      ${port.mode === 'uplink' ? 'bg-blue-500' : port.mode === 'trunk' ? 'bg-purple-500' : 'bg-gray-400'}`}>
                      {port.type === 'sfp+' ? '+' : '◆'}
                    </div>
                    <span className="text-xs font-mono font-bold text-gray-700 dark:text-gray-300">{port.port_id}</span>
                  </div>
                  <select value={port.mode} onChange={e => updatePort(port.port_id, { mode: e.target.value as PortConfig['mode'] })}
                    onClick={e => e.stopPropagation()}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs">
                    <option value="uplink">Uplink / Trunk</option>
                    <option value="access">Access</option>
                    <option value="trunk">Trunk (Multi-VLAN)</option>
                    <option value="disabled">Devre Dışı</option>
                  </select>
                  <select value={port.native_vlan} onChange={e => updatePort(port.port_id, { native_vlan: parseInt(e.target.value) })}
                    onClick={e => e.stopPropagation()}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs">
                    {vlanOptions.map(v => <option key={v.vlan_id} value={v.vlan_id}>VLAN {v.vlan_id} {v.name ? `- ${v.name}` : ''}</option>)}
                  </select>
                  <input type="text" value={port.description} onChange={e => { e.stopPropagation(); updatePort(port.port_id, { description: e.target.value }); }}
                    onClick={e => e.stopPropagation()}
                    placeholder="Açıklama (örn: Core Uplink)"
                    className="flex-1 min-w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Copper Ports — condensed table */}
        <div>
          <div className="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-3">
            Bakır Portlar ({copperPorts.length} port)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  <th className="px-2 py-2 text-left w-8">
                    <input type="checkbox"
                      checked={copperPorts.every(p => selectedPorts.includes(p.port_id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedPorts(prev => [...new Set([...prev, ...copperPorts.map(p => p.port_id)])]);
                        else setSelectedPorts(prev => prev.filter(id => !copperPorts.map(p => p.port_id).includes(id)));
                      }}
                      className="accent-blue-600" />
                  </th>
                  <th className="px-2 py-2 text-left">Port</th>
                  <th className="px-2 py-2 text-left">Mod</th>
                  <th className="px-2 py-2 text-left">VLAN</th>
                  <th className="px-2 py-2 text-left">Açıklama</th>
                  {state.model.poe && <th className="px-2 py-2 text-center">PoE</th>}
                  <th className="px-2 py-2 text-center">Kapalı</th>
                </tr>
              </thead>
              <tbody>
                {copperPorts.map((port, idx) => (
                  <tr key={port.port_id}
                    className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer
                      ${selectedPorts.includes(port.port_id) ? 'bg-yellow-50 dark:bg-yellow-900/10' : idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}
                      hover:bg-blue-50 dark:hover:bg-blue-900/10`}
                    onClick={() => setSelectedPorts(prev => prev.includes(port.port_id) ? prev.filter(x => x !== port.port_id) : [...prev, port.port_id])}>
                    <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedPorts.includes(port.port_id)}
                        onChange={e => setSelectedPorts(prev => e.target.checked ? [...prev, port.port_id] : prev.filter(x => x !== port.port_id))}
                        className="accent-blue-600" />
                    </td>
                    <td className="px-2 py-1.5 font-mono font-bold text-gray-700 dark:text-gray-300">{port.port_id}</td>
                    <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                      <select value={port.mode} onChange={e => updatePort(port.port_id, { mode: e.target.value as PortConfig['mode'] })}
                        className="px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs">
                        <option value="access">Access</option>
                        <option value="trunk">Trunk</option>
                        <option value="disabled">Kapalı</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                      <select value={port.native_vlan} onChange={e => updatePort(port.port_id, { native_vlan: parseInt(e.target.value) })}
                        className="px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs">
                        {vlanOptions.map(v => <option key={v.vlan_id} value={v.vlan_id}>VLAN {v.vlan_id} {v.name ? `(${v.name})` : ''}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                      <input type="text" value={port.description}
                        onChange={e => updatePort(port.port_id, { description: e.target.value })}
                        placeholder="açıklama"
                        className="w-full min-w-24 px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs" />
                    </td>
                    {state.model.poe && (
                      <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={port.poe_enabled}
                          onChange={e => updatePort(port.port_id, { poe_enabled: e.target.checked })}
                          className="accent-orange-500 w-3.5 h-3.5" />
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={port.admin_down}
                        onChange={e => updatePort(port.port_id, { admin_down: e.target.checked })}
                        className="accent-red-500 w-3.5 h-3.5" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderStep6 = () => {
    const fiberPorts = state.ports.filter(p => p.type === 'sfp' || p.type === 'sfp+');
    const copperPorts = state.ports.filter(p => p.type === 'copper');
    const vlanOptions = state.vlans.filter(v => v.vlan_id > 0);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Uplink ve LAG Grupları</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Fiber uplink portları ve Link Aggregation (LACP/LAG) grupları.</p>
          </div>
          <button onClick={addLag} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            <Plus className="w-4 h-4" /> LAG Grubu Ekle
          </button>
        </div>

        {/* Fiber port summary */}
        {fiberPorts.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <div className="font-semibold text-blue-800 dark:text-blue-300 text-sm mb-3">Fiber / SFP Port Durumu</div>
            <div className="flex flex-wrap gap-3">
              {fiberPorts.map(p => (
                <div key={p.port_id} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-blue-200 dark:border-blue-700 text-xs">
                  <span className={`w-2.5 h-2.5 rounded-full ${p.mode === 'uplink' ? 'bg-blue-500' : p.mode === 'trunk' ? 'bg-purple-500' : p.admin_down ? 'bg-gray-400' : 'bg-green-500'}`} />
                  <span className="font-mono font-bold text-gray-700 dark:text-gray-300">{p.port_id}</span>
                  <span className="text-gray-500 dark:text-gray-400 capitalize">{p.mode}</span>
                  {p.description && <span className="text-gray-400">— {p.description}</span>}
                  {state.lagGroups.some(l => l.ports.includes(p.port_id)) && (
                    <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded font-medium">
                      LAG {state.lagGroups.find(l => l.ports.includes(p.port_id))?.id}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LAG Groups */}
        {state.lagGroups.length === 0 && (
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Info className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              LAG (Link Aggregation Group), birden fazla portu birleştirerek bant genişliği artırır ve yedeklilik sağlar. LACP (802.3ad) ile dinamik olarak yönetilebilir.
            </p>
          </div>
        )}

        {state.lagGroups.map(lag => (
          <div key={lag.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-xs font-bold">
                  {lag.id}
                </span>
                LAG {lag.id}
              </div>
              <button onClick={() => removeLag(lag.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Grup Adı</label>
                <input type="text" value={lag.name} onChange={e => updateLag(lag.id, { name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">LACP Modu</label>
                <select value={lag.mode} onChange={e => updateLag(lag.id, { mode: e.target.value as LagGroup['mode'] })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                  <option value="active">Active (Tavsiye)</option>
                  <option value="passive">Passive</option>
                  <option value="static">Static (LACP'siz)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Native VLAN</label>
                <select value={lag.native_vlan} onChange={e => updateLag(lag.id, { native_vlan: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                  {vlanOptions.map(v => <option key={v.vlan_id} value={v.vlan_id}>VLAN {v.vlan_id} {v.name ? `(${v.name})` : ''}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Gruba Eklenecek Portlar</label>
                <div className="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 min-h-12">
                  {[...fiberPorts, ...copperPorts].map(p => (
                    <button key={p.port_id}
                      onClick={() => {
                        const ports = lag.ports.includes(p.port_id)
                          ? lag.ports.filter(x => x !== p.port_id)
                          : [...lag.ports, p.port_id];
                        updateLag(lag.id, { ports });
                      }}
                      className={`px-2 py-1 rounded text-xs font-mono font-bold transition-colors
                        ${lag.ports.includes(p.port_id)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900/30'}`}>
                      {p.port_id}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Allowed VLANs (boş = tümü)</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 min-h-12">
                  {vlanOptions.map(v => (
                    <button key={v.vlan_id}
                      onClick={() => {
                        const vids = lag.allowed_vlans.includes(v.vlan_id)
                          ? lag.allowed_vlans.filter(x => x !== v.vlan_id)
                          : [...lag.allowed_vlans, v.vlan_id];
                        updateLag(lag.id, { allowed_vlans: vids });
                      }}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors
                        ${lag.allowed_vlans.includes(v.vlan_id)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'}`}>
                      {v.vlan_id}{v.name ? ` ${v.name}` : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStep7 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">İzleme ve Spanning Tree</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Syslog, Spanning Tree ve gelişmiş ağ protokolleri.</p>
      </div>

      {/* Syslog */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Syslog / Merkezi Log</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Syslog Sunucu IP</label>
            <input type="text" value={state.syslogServer} onChange={e => update({ syslogServer: e.target.value })}
              placeholder="192.168.1.5"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Şiddet Seviyesi</label>
            <select value={state.syslogSeverity} onChange={e => update({ syslogSeverity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              <option value="emergency">Emergency (0)</option>
              <option value="alert">Alert (1)</option>
              <option value="critical">Critical (2)</option>
              <option value="error">Error (3)</option>
              <option value="warning">Warning (4) — Tavsiye</option>
              <option value="notice">Notice (5)</option>
              <option value="informational">Informational (6)</option>
              <option value="debug">Debug (7)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Spanning Tree */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm flex items-center gap-2">
          Spanning Tree Protokolü
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— Layer 2 döngü koruması</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">STP Modu</label>
            <select value={state.spanningTreeMode} onChange={e => update({ spanningTreeMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              <option value="mstp">MSTP — Multiple STP (Tavsiye)</option>
              <option value="rapid-pvst">Rapid-PVST+ (Cisco uyumlu)</option>
              <option value="rstp">RSTP — Rapid STP</option>
              <option value="none">Devre Dışı (DİKKAT)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bridge Priority</label>
            <select value={state.spanningTreePriority} onChange={e => update({ spanningTreePriority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
              <option value="4096">4096 — Root Switch</option>
              <option value="8192">8192 — Secondary Root</option>
              <option value="16384">16384</option>
              <option value="32768">32768 — Varsayılan</option>
              <option value="49152">49152</option>
              <option value="61440">61440</option>
            </select>
          </div>
        </div>
        {state.spanningTreeMode === 'none' && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-xs text-red-700 dark:text-red-400">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>STP devre dışıyken yanlış kablo bağlantısı tüm ağı çökertebilir! Sadece kesinlikle gerektiğinde kapatın.</span>
          </div>
        )}
      </div>

      {/* Summary / Checklist */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm mb-3">Konfigürasyon Özeti</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {[
            { ok: !!state.hostname, label: 'Hostname tanımlı' },
            { ok: !!state.mgmtGateway, label: 'Default gateway tanımlı' },
            { ok: !!state.ntpServer1, label: 'NTP sunucusu tanımlı' },
            { ok: !!state.syslogServer, label: 'Syslog sunucusu tanımlı' },
            { ok: state.sshEnabled, label: 'SSH etkin' },
            { ok: !state.telnetEnabled, label: 'Telnet kapalı' },
            { ok: !state.httpEnabled, label: 'HTTP kapalı' },
            { ok: !!state.enableSecret, label: 'Enable secret tanımlı' },
            { ok: !!state.bannerMotd, label: 'Banner MOTD tanımlı' },
            { ok: state.vlans.length > 1, label: 'Birden fazla VLAN tanımlı' },
            { ok: state.spanningTreeMode !== 'none', label: 'Spanning Tree aktif' },
            { ok: state.snmpEnabled && state.snmpVersion === 'v3', label: 'SNMPv3 yapılandırıldı' },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-2 ${item.ok ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {item.ok ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />}
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep8 = () => (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Üretilen Konfigürasyon</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {state.model?.name} — {state.model?.family === 'cx' ? 'ArubaOS-CX' : 'ArubaOS-Switch (ProVision)'} formatı
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setGeneratedConfig(generateConfig(state)); }}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium transition-colors">
            <RefreshCw className="w-4 h-4" /> Yeniden Üret
          </button>
          <button onClick={copyConfig}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
              ${copied ? 'bg-green-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Kopyalandı!' : 'Kopyala'}
          </button>
          <button onClick={downloadConfig}
            className="flex items-center gap-2 px-3 py-2 bg-gray-800 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-900 dark:hover:bg-gray-500 text-sm font-medium transition-colors">
            <Download className="w-4 h-4" /> .txt İndir
          </button>
        </div>
      </div>

      <pre className="bg-gray-900 text-green-400 rounded-xl p-4 text-xs font-mono overflow-auto max-h-[60vh] leading-relaxed border border-gray-700 whitespace-pre-wrap">
        {generatedConfig}
      </pre>
    </div>
  );

  const stepContent: Record<number, () => React.ReactNode> = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
    5: renderStep5,
    6: renderStep6,
    7: renderStep7,
    8: renderStep8,
  };

  const canProceed = () => {
    if (step === 1) return !!state.model;
    if (step === 2) return !!state.hostname;
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-blue-600 rounded-xl text-white">
              <Zap className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Switch Config Wizard</h1>
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">Aruba</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-12">Aruba 2530, 1930, 6000 serisi için hızlı ve güvenli konfigürasyon oluşturucu</p>
        </div>

        {/* Step indicator */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {STEP_LABELS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => { if (isDone || s.id === 1) setStep(s.id); }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all
                      ${isActive ? 'bg-blue-600 text-white shadow' : isDone ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/60 cursor-pointer' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500'}`}>
                    {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    {s.label}
                  </button>
                  {i < STEP_LABELS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          {stepContent[step]?.()}

          {/* Navigation */}
          {step !== 8 && (
            <div className="flex justify-between mt-8 pt-5 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setStep(s => Math.max(1, s - 1))}
                disabled={step === 1}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" /> Geri
              </button>

              {step < 7 ? (
                <button
                  onClick={() => setStep(s => Math.min(7, s + 1))}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  İleri <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                  <CheckCircle className="w-4 h-4" /> Konfigürasyonu Üret
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwitchConfigWizard;
